/**
 * Stock take item capacity: up to {@link STOCK_TAKE_BASE_ITEM_LIMIT} list items and
 * {@link STOCK_TAKE_TOTAL_ITEM_LIMIT} lines in total. Unique scans fill any remaining
 * slots (e.g. 200 list + 400 unique = 600).
 */
export const STOCK_TAKE_BASE_ITEM_LIMIT = 500;

/** Default user-facing cap when creating stock takes or schedules (max remains {@link STOCK_TAKE_BASE_ITEM_LIMIT}). */
export const STOCK_TAKE_DEFAULT_BASE_ITEM_LIMIT = 250;

export const STOCK_TAKE_TOTAL_ITEM_LIMIT = 600;

/** Unique scan slots remaining when list items are already at {@link STOCK_TAKE_BASE_ITEM_LIMIT}. */
export const STOCK_TAKE_UNIQUE_ITEM_LIMIT = STOCK_TAKE_TOTAL_ITEM_LIMIT - STOCK_TAKE_BASE_ITEM_LIMIT;

/** Maximum schedule entries per store (enforced on the frontend). */
export const STOCK_TAKE_MAX_SCHEDULE_COUNT = 50;

/** Page size when scanning Typesense for client-side advanced filters during stock take population. */
export const STOCK_TAKE_CLIENT_FILTER_PAGE_SIZE = 250;

/** Safety cap on Typesense pages scanned for client-side advanced filters during stock take population. */
export const STOCK_TAKE_CLIENT_FILTER_MAX_PAGES = 150;

export interface IStockTakeItemCapacity {
  base: number;
  total: number;
  unique: number;
}

/**
 * Clamp a user-entered base-item limit to the allowed range.
 */
export function clampBaseItemLimit(limit: number): number {
  if (!Number.isFinite(limit) || limit < 1) {
    return 1;
  }
  return Math.min(Math.floor(limit), STOCK_TAKE_BASE_ITEM_LIMIT);
}

/**
 * Resolve the store default max-item limit. Missing / invalid Firestore values use
 * {@link STOCK_TAKE_DEFAULT_BASE_ITEM_LIMIT}.
 */
export function resolveStockTakeDefaultMaxItemLimit(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return STOCK_TAKE_DEFAULT_BASE_ITEM_LIMIT;
  }
  return clampBaseItemLimit(value);
}

/**
 * Whether more non-UNIQUE (list) items can be added.
 */
export function canAddBaseItems(capacity: IStockTakeItemCapacity, adding: number = 1): boolean {
  if (adding <= 0) {
    return true;
  }
  return (
    capacity.base + adding <= STOCK_TAKE_BASE_ITEM_LIMIT &&
    capacity.total + adding <= STOCK_TAKE_TOTAL_ITEM_LIMIT
  );
}

/**
 * Whether more UNIQUE scanned items can be added (any remaining slot up to total cap).
 */
export function canAddUniqueItems(capacity: IStockTakeItemCapacity, adding: number = 1): boolean {
  if (adding <= 0) {
    return true;
  }
  return capacity.total + adding <= STOCK_TAKE_TOTAL_ITEM_LIMIT;
}

export function stockTakeBaseLimitMessage(): string {
  return `A stock take can include at most ${STOCK_TAKE_BASE_ITEM_LIMIT} list items.`;
}

export function stockTakeDuplicateItemsMessage(): string {
  return 'The selected items are already on this stock take. Duplicate items will not be added again.';
}

export function stockTakeSkippedDuplicateItemsMessage(skippedCount: number): string {
  if (skippedCount === 1) {
    return '1 selected item was already on this stock take and was not added again.';
  }
  return `${skippedCount} selected items were already on this stock take and were not added again.`;
}

export function stockTakePartialAddLimitMessage(added: number, requested: number): string {
  return `Added ${added} of ${requested} item(s). This stock take is at its list item limit (${STOCK_TAKE_BASE_ITEM_LIMIT}).`;
}

export function stockTakeUniqueLimitMessage(): string {
  return `This stock take already has the maximum of ${STOCK_TAKE_TOTAL_ITEM_LIMIT} items (${STOCK_TAKE_BASE_ITEM_LIMIT} list items plus unique scans).`;
}

export function stockTakeMutualExclusivePopulationMessage(): string {
  return 'Choose either a stock filter or "items not counted in X days", not both.';
}

/**
 * Whether another schedule entry can be created for the store.
 */
export function canAddStockTakeSchedule(currentCount: number): boolean {
  return currentCount < STOCK_TAKE_MAX_SCHEDULE_COUNT;
}

export function stockTakeScheduleLimitMessage(): string {
  return `A store can have at most ${STOCK_TAKE_MAX_SCHEDULE_COUNT} stock take schedules. Delete an existing schedule to add a new one.`;
}

export function stockTakeScheduleLimitTooltip(): string {
  return `Stock take schedules are limited to ${STOCK_TAKE_MAX_SCHEDULE_COUNT} per store.`;
}

/**
 * Remaining list (non-UNIQUE) item slots before hitting the base cap.
 */
export function remainingBaseItemSlots(capacity: IStockTakeItemCapacity): number {
  return Math.max(
    0,
    Math.min(
      STOCK_TAKE_BASE_ITEM_LIMIT - capacity.base,
      STOCK_TAKE_TOTAL_ITEM_LIMIT - capacity.total,
    ),
  );
}

/**
 * Remaining UNIQUE scan slots before hitting the total cap.
 */
export function remainingUniqueItemSlots(capacity: IStockTakeItemCapacity): number {
  return Math.max(0, STOCK_TAKE_TOTAL_ITEM_LIMIT - capacity.total);
}

export function isUniqueScanAllowed(total: number, unique: number): boolean {
  const safeTotal = Math.max(0, Math.floor(total));
  const safeUnique = Math.max(0, Math.min(Math.floor(unique), safeTotal));
  return canAddUniqueItems(
    { total: safeTotal, unique: safeUnique, base: safeTotal - safeUnique },
    1,
  );
}
